/**
 * Veilige localStorage-toegang. Lezen en schrijven mogen de app nooit laten crashen:
 * niet bij corrupte data, geblokkeerde opslag (privémodus) of een vol geheugen (quota).
 */

/**
 * Leest een bewaarde waarde en laat die door `parse` controleren. Alles wat ontbreekt,
 * corrupt is of niet door `parse` komt (die geeft dan `undefined`) wordt `fallback`.
 */
export function readStored<T>(key: string, fallback: T, parse: (raw: unknown) => T | undefined): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    let value: unknown = raw;
    try {
      value = JSON.parse(raw);
    } catch {
      // Gewone strings (bv. "km") zonder JSON-aanhalingstekens laten we zo door.
    }
    const parsed = parse(value);
    return parsed === undefined ? fallback : parsed;
  } catch {
    return fallback;
  }
}

/** Geeft true als het schrijven lukte, false bij quota- of toegangsfouten. */
export function writeStored(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/** Parser die alleen waarden uit een vaste lijst toelaat. */
export function oneOf<T extends string>(allowed: readonly T[]) {
  return (raw: unknown): T | undefined =>
    typeof raw === 'string' && (allowed as readonly string[]).includes(raw) ? (raw as T) : undefined;
}

/** Parser voor lijsten: elk element moet door `parseItem`; ongeldige elementen worden weggelaten. */
export function listOf<T>(parseItem: (raw: unknown) => T | undefined) {
  return (raw: unknown): T[] | undefined =>
    Array.isArray(raw) ? raw.map(parseItem).filter((x): x is T => x !== undefined) : undefined;
}
